import dayjs from 'dayjs';

/**
 * Formata um inteiro com separador de milhar brasileiro.
 *
 * @param value - Valor numérico
 * @returns Número formatado (ex.: `"12.480"`)
 */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value);
}

/**
 * Formata um número em notação compacta, para rótulos de eixo onde o valor exato
 * ocuparia espaço demais.
 *
 * @param value - Valor numérico
 * @returns Número abreviado (ex.: `"413 mil"`, `"12,4 mi"`)
 *
 * @example
 * ```ts
 * formatCompactCount(413_000); // "413 mil"
 * ```
 */
export function formatCompactCount(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Formata um percentual com no máximo uma casa decimal, já com o símbolo.
 *
 * O símbolo pertence ao formatador, não ao texto de i18n — assim as chaves que exibem
 * percentual interpolam `{{percent}}` sozinho e não repetem o `%` cada uma por si.
 *
 * @param value - Percentual em pontos (0–100)
 * @returns Percentual formatado (ex.: `"82%"`, `"12,4%"`)
 *
 * @example
 * ```ts
 * formatPercent(82); // "82%"
 * ```
 */
export function formatPercent(value: number): string {
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value)}%`;
}

/**
 * Converte bytes na unidade legível mais próxima.
 *
 * @param bytes - Tamanho em bytes
 * @returns Tamanho formatado (ex.: `"2,4 MB"`)
 */
export function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const formatted = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: value < 10 && unitIndex > 0 ? 1 : 0,
  }).format(value);

  return `${formatted} ${units[unitIndex]}`;
}

/**
 * Formata uma data ISO em `DD/MM/AAAA`.
 *
 * @param iso - Data em ISO 8601
 * @returns Data formatada, ou string vazia quando a entrada é inválida
 */
export function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
}

/**
 * Descarta o deslocamento de fuso declarado numa data ISO, preservando o relógio recebido.
 *
 * ⚠️ **GAMBIARRA TEMPORÁRIA — compensa um defeito do backend. Apague quando ele for
 * corrigido.**
 *
 * O servidor carimba `createdAt`/`updatedAt` com a hora local dele e serializa declarando
 * UTC. Um documento enviado às 09:18 em Brasília chega como
 * `2026-08-07T09:18:13.2747927+00:00`: o relógio é local, o `+00:00` diz que é UTC. Um
 * cliente correto lê 09:18 UTC e exibe 06:18 para quem está em UTC-3 — o defeito relatado.
 *
 * No cliente os dois casos são indistinguíveis: nada no payload separa "09:18 que é local"
 * de "09:18 que é UTC de verdade". A única compensação possível é ignorar o deslocamento e
 * tratar o relógio recebido como local, que é o que esta função faz. De quebra, a exibição
 * deixa de variar com o fuso do observador.
 *
 * **Isto quebra em silêncio no dia em que o backend passar a mandar UTC de verdade:** um
 * `12:18Z` legítimo passará a aparecer como 12:18, três horas à frente. Nenhum teste detecta
 * essa virada — a detecção é humana. Se você chegou aqui investigando um horário adiantado,
 * é este o motivo: apague esta função e devolva `formatDateTime` a `dayjs(iso)`.
 *
 * @param iso - Data em ISO 8601, com ou sem deslocamento declarado
 * @returns A mesma string sem o sufixo de deslocamento
 */
function dropDeclaredOffset(iso: string): string {
  return iso.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '');
}

/**
 * Formata uma data ISO em `DD/MM/AAAA HH:mm`, exibindo o relógio que veio no payload.
 *
 * O deslocamento declarado é descartado de propósito — ver {@link dropDeclaredOffset}, que
 * explica qual defeito de backend isso compensa e quando remover.
 *
 * @param iso - Data em ISO 8601
 * @returns Data e hora formatadas, ou string vazia quando a entrada é inválida
 */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '';
  const parsed = dayjs(dropDeclaredOffset(iso));
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '';
}

/**
 * Rótulo curto de eixo para uma data ISO (`DD/MM`).
 *
 * @param iso - Data em ISO 8601
 * @returns Dia e mês (ex.: `"14/07"`)
 */
export function formatAxisDate(iso: string): string {
  return dayjs(iso).format('DD/MM');
}
